import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { UPGRADE } from '@/constants/testIds';
import { Sparkles, Check, Loader2, Lock } from 'lucide-react';
import { toast } from 'sonner';

const PREMIUM_FEATURES = [
    'AI conflict resolution suggestions',
    'Per-person satisfaction scores',
    'Personality balance indicators',
    'Regenerate with emphasis on a person or vibe',
    'Unlimited AI itinerary generations',
    'Unlimited participants',
    'AI cost breakdown by category',
    'Shared cost estimator',
    'PDF export',
];

export default function UpgradeModal({ open, onOpenChange, tripId, tripName, onPaymentStarted }) {
    const [loading, setLoading] = useState(false);

    const startCheckout = async () => {
        setLoading(true);
        try {
            const { data } = await api.post(`/trips/${tripId}/checkout`, {
                origin_url: window.location.origin,
            });
            if (!data?.url) throw new Error('No checkout URL');
            onPaymentStarted?.(data.session_id);
            window.location.href = data.url;
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not start checkout');
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid={UPGRADE.modal} className="sm:max-w-md rounded-3xl border-[var(--ns-border)]">
                <DialogHeader>
                    <div className="mx-auto w-12 h-12 rounded-2xl flex items-center justify-center mb-3" style={{ background: 'var(--ns-soft)' }}>
                        <Sparkles className="text-[var(--ns-clay)]" />
                    </div>
                    <DialogTitle className="text-center font-display text-2xl tracking-tight">
                        Upgrade <span style={{ color: 'var(--ns-clay)' }}>{tripName || 'this trip'}</span> to Premium
                    </DialogTitle>
                    <p className="text-center text-sm text-[var(--ns-text2)] mt-1">
                        One-time payment per trip — unlock conflict resolution, full itinerary insights, and exports.
                    </p>
                </DialogHeader>

                <div className="rounded-2xl border border-[var(--ns-border)] bg-[var(--ns-soft)] p-4 my-3 text-center">
                    <div className="text-xs uppercase tracking-[0.18em] text-[var(--ns-text3)]">One-time</div>
                    <div data-testid={UPGRADE.priceLabel} className="font-display text-4xl font-semibold tracking-tight mt-1">
                        $6.99 <span className="text-base font-normal text-[var(--ns-text2)]">SGD</span>
                    </div>
                </div>

                <ul className="space-y-2 mt-2">
                    {PREMIUM_FEATURES.map((f) => (
                        <li key={f} data-testid={UPGRADE.featureItem} className="flex items-start gap-2 text-sm text-[var(--ns-text)]">
                            <Check size={16} className="text-[var(--ns-success,#3F8E5B)] mt-0.5 flex-shrink-0" />
                            <span>{f}</span>
                        </li>
                    ))}
                </ul>

                <Button
                    onClick={startCheckout}
                    disabled={loading}
                    data-testid={UPGRADE.payButton}
                    className="w-full rounded-full h-12 text-white font-medium mt-4"
                    style={{ background: 'var(--ns-navy)' }}
                >
                    {loading ? <><Loader2 size={16} className="mr-2 animate-spin" /> Redirecting…</> : <><Lock size={14} className="mr-2" /> Pay $6.99 SGD with Stripe</>}
                </Button>
                <div className="flex items-center justify-center gap-3 mt-2 text-xs text-[var(--ns-text3)]">
                    <Link
                        to="/premium"
                        data-testid={UPGRADE.modalCompareLink}
                        onClick={() => onOpenChange(false)}
                        className="underline-offset-4 hover:underline hover:text-[var(--ns-clay)] transition"
                    >
                        Compare Free vs Premium features →
                    </Link>
                </div>
                <p className="text-xs text-center text-[var(--ns-text3)] mt-1">Secure checkout via Stripe · You&apos;ll return here after payment.</p>
            </DialogContent>
        </Dialog>
    );
}
